import React from 'react';
import { Box, Paper, Typography, Button, TextField, Stack, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase';
import { getPreviewSiteConfigByCode } from '../../firebase/firebase-delegate';

type ListWebsiteIdsRequest = {
  filterExampleSites?: boolean;
};

const SuperAdminDashboard: React.FC = () => {
  const [siteId, setSiteId] = React.useState('');
  const [previewDialogOpen, setPreviewDialogOpen] = React.useState(false);
  const [previewCode, setPreviewCode] = React.useState('');
  const [previewError, setPreviewError] = React.useState('');
  const [creatingPreviewSite, setCreatingPreviewSite] = React.useState(false);
  const navigate = useNavigate();

  const useListWebsiteIds = useQuery({
    queryKey: ['websiteIds'],
    queryFn: async () => {
      const listWebsiteIds = httpsCallable<ListWebsiteIdsRequest, string[]>(functions, 'listWebsiteIds');
      const result = await listWebsiteIds({ filterExampleSites: false });
      return result.data;
    }
  });

  const handleManageClick = () => {
    if (siteId.trim()) {
      navigate(`/admin/superadmin/manage/${siteId.trim()}`);
    }
  };

  const handleQuickAccessClick = (id: string) => {
    navigate(`/admin/superadmin/manage/${id}`);
  };

  const handleAnalyticsClick = () => {
    navigate('/admin/superadmin/analytics', { state: { websiteIds } });
  };

  const handlePreviewCodeChange = (value: string) => {
    const normalizedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    setPreviewCode(normalizedValue);
    if (previewError) {
      setPreviewError('');
    }
  };

  const handleCreatePreviewSite = async () => {
    if (!/^[A-Z0-9]{1,6}$/.test(previewCode)) {
      setPreviewError('Enter an alphanumeric code up to 6 characters.');
      return;
    }

    try {
      setCreatingPreviewSite(true);
      setPreviewError('');
      const existingPreviewSite = await getPreviewSiteConfigByCode(previewCode);
      if (existingPreviewSite) {
        setPreviewError('A preview site with this code already exists.');
        return;
      }

      setPreviewDialogOpen(false);
      navigate(`/admin/superadmin/preview-site/${previewCode}`);
    } catch {
      setPreviewError('Failed to check preview site.');
    } finally {
      setCreatingPreviewSite(false);
    }
  };

  const handleViewPreviewSite = () => {
    if (!previewCode) {
      return;
    }
    setPreviewDialogOpen(false);
    navigate(`/admin/superadmin/preview-site/${previewCode}`);
  };

  const previewSiteAlreadyExists = previewError.toLowerCase().includes('already exists');

  const isLoading = useListWebsiteIds.isLoading;
  const isError = useListWebsiteIds.isError;
  const websiteIds = useListWebsiteIds.data ?? [];

  if (isLoading) return <p>Loading...</p>;
  if (isError) return <p>Error loading sites.</p>;

  return (
    <Box p={4}>
      <Typography variant="h3" gutterBottom>
        SuperAdmin Dashboard
      </Typography>

      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Analytics
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAnalyticsClick}>
          View Cross-Business Analytics
        </Button>
      </Paper>

      {/* Manual entry */}
      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Manage a Site
        </Typography>
        <TextField
          fullWidth
          label="Enter Site ID"
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleManageClick}
          disabled={!siteId.trim()}
        >
          Go to Site
        </Button>
      </Paper>

      <Paper elevation={3} sx={{ p: 4, maxWidth: 500, mt: 4, backgroundColor: '#000', color: '#fff' }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#fff' }}>
          Manage a Preview Site
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.8)' }}>
          Reserve a preview code, then configure its branding and homepage content.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            setPreviewError('');
            setPreviewDialogOpen(true);
          }}
          sx={{
            color: '#fff',
            borderColor: '#fff',
            '&:hover': {
              borderColor: '#fff',
              backgroundColor: 'rgba(255,255,255,0.08)',
            },
          }}
        >
          Enter Site ID
        </Button>
      </Paper>

      {/* Quick-access list */}
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Quick Links
        </Typography>
        <Stack spacing={2} mt={2}>
          {websiteIds.map((id) => (
            <Button
              key={id}
              variant="outlined"
              onClick={() => handleQuickAccessClick(id)}
              sx={{ textTransform: 'none' }}
            >
              {id}
            </Button>
          ))}
        </Stack>
      </Paper>

      <Dialog open={previewDialogOpen} onClose={() => !creatingPreviewSite && setPreviewDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Enter Site ID</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Preview Code"
            value={previewCode}
            onChange={(e) => handlePreviewCodeChange(e.target.value)}
            margin="normal"
            inputProps={{ maxLength: 6 }}
            helperText="Alphanumeric only, maximum 6 characters."
          />
          {previewError && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="error">{previewError}</Alert>
              {previewSiteAlreadyExists && (
                <Button variant="text" onClick={handleViewPreviewSite} sx={{ mt: 1, px: 0 }}>
                  View Site
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)} disabled={creatingPreviewSite}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreatePreviewSite} disabled={creatingPreviewSite}>
            {creatingPreviewSite ? 'Creating...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminDashboard;
